import { Outlet, useNavigate } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { ArrowRight, LogOut } from "lucide-react"

import { AppLayout } from "@/components/layout/AppLayout"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { MartyNota } from "@/components/MartyNota"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { LineaDeMargen, Logotype } from "@/components/layout/Logotype"
import { useAuth, useRbac } from "@/lib/auth"
import { paisDeLaPantalla } from "@/lib/entities"
import { I18nProvider, t } from "@/lib/i18n"
import { VozProvider } from "@/lib/voz"

import { fetchPasos } from "@/routes/onboarding/api"

/**
 * EL TRASPASO, A PANTALLA COMPLETA Y SIN LA APLICACIÓN ALREDEDOR.
 *
 * Cesar: «debería de ser que una vez te logueas sólo te abre la parte de
 * primeros pasos (sin todo lo otro), y te aparece sin nada de lo de arriba
 * tampoco, porque recién estás creando cada cosa».
 *
 * ===========================================================================
 * POR QUÉ TENÍA RAZÓN, Y NO ES UNA PREFERENCIA ESTÉTICA
 * ===========================================================================
 *
 * Primeros pasos vivía dentro de `AppLayout`, así que quien entraba por primera
 * vez veía **el riel entero de siete secciones y la barra de alcance** alrededor
 * del cuestionario. Y las tres cosas que esa barra pide no existen todavía:
 *
 *   - El selector de SOCIEDAD está vacío, o en ámbar pidiendo que elijas una —
 *     cuando crearla es literalmente el primer paso del cuestionario.
 *   - El riel ofrece Finanzas, Contabilidad y Holding, que sin datos devuelven
 *     pantallas vacías. Siete puertas que no llevan a ningún sitio invitan a
 *     salirse del traspaso justo cuando lo importante es terminarlo.
 *   - La nota de Marty, el tema, el idioma, las capacidades del rol… todo eso es
 *     el mobiliario de un sitio en el que todavía no vives.
 *
 * Así que el traspaso deja de ser una pantalla DENTRO del producto y pasa a ser
 * lo que de verdad es: la puerta. Mismo soporte que el login —rayado de libro
 * mayor y la línea de margen de arriba abajo—, porque son las dos piezas que se
 * ven antes de que el producto exista.
 *
 * ---------------------------------------------------------------------------
 * LO QUE SÍ SE QUEDA, Y POR QUÉ NO ES INCUMPLIR EL «SIN NADA DE LO DE ARRIBA»
 *
 * Tres cosas, y ninguna es un filtro:
 *
 *   El identificador, para saber dónde estás.
 *   Cerrar sesión, porque una pantalla sin salida no es una pantalla.
 *   «Ir a la aplicación», que aparece SÓLO cuando no queda nada pendiente.
 *
 * Ese último es el que hace que esto no sea una jaula. Mientras falte algo, la
 * salida es terminar; en cuanto no falta nada, la puerta se abre sola y se ve.
 * Quien quiera salirse antes puede: los pasos cuyo trabajo vive en otra pantalla
 * —cargar la plantilla, revisar la cola— enlazan ahí y ahí sí está el producto
 * entero.
 *
 * ===========================================================================
 * PERO SÓLO LA PRIMERA VEZ. LA SEGUNDA ES UNA PANTALLA MÁS
 * ===========================================================================
 *
 * Cesar, volviendo al traspaso desde dentro: «cuando regreso a primeros pasos
 * se desaparece todo; debe desaparecer todo sólo la primera vez, y una vez ya
 * estés adentro sí debería salir todo a la izquierda y arriba».
 *
 * Tenía razón y el error era mío: quitar el mobiliario estaba bien razonado
 * para quien ACABA DE LLEGAR, y se aplicó a la RUTA en vez de a la situación.
 * El traspaso no se termina de una sentada —quedan pasos apartados, saldos que
 * llegan en enero, una planilla que sube otra persona— así que se vuelve a él
 * muchas veces, y cada vuelta te dejaba sin riel y sin barra de alcance: la
 * única salida era «Cerrar sesión» o el botón que sólo aparece cuando ya no
 * falta nada. Una pantalla sin salida, que es justo lo que este layout dice
 * evitar tres párrafos más arriba.
 *
 * LA CONDICIÓN ES UN DATO, NO POR DÓNDE ENTRASTE. Se mira si el cliente tiene
 * **alguna sociedad**, y es la misma razón por la que el mobiliario sobraba:
 * los tres argumentos de arriba —el selector vacío, el riel que lleva a
 * pantallas en blanco, el idioma que sigue a una sociedad que no existe— son
 * ciertos mientras NO HAY NINGUNA SOCIEDAD, y dejan de serlo en cuanto hay una.
 * Crear la primera es el paso 1. Así que el producto aparece exactamente cuando
 * empieza a tener algo que enseñar.
 *
 * Y no se mira «si quedan pasos pendientes»: quedarían durante semanas —los
 * saldos de apertura, la planilla del mes— y eso dejaría sin navegación a quien
 * ya lleva media contabilidad cargada. Tampoco se mira de dónde venías, que es
 * estado y se pierde al recargar.
 */
export function TraspasoLayout() {
  const { activeMembership } = useRbac()
  const {
    entities, entitiesLoading, activeEntityId, profile, activeTenantId, signOut,
    setActiveTenantId,
  } = useAuth()
  const navigate = useNavigate()

  // Mismo par de ejes que en `AppLayout`: el país sigue a la sociedad y el
  // idioma a la persona. Ver `lib/i18n.tsx`.
  const pais = paisDeLaPantalla(entities, activeEntityId, activeMembership?.country)

  const pasos = useQuery({
    queryKey: ["onboarding-pasos", activeTenantId],
    queryFn: () => fetchPasos(),
    enabled: Boolean(activeTenantId),
  })
  const terminado = pasos.data ? pasos.data.pendientes_activos === 0 : false

  /*
    YA HAY PRODUCTO: el traspaso pasa a ser una pantalla de la aplicación, con
    su riel y su barra. `AppLayout` monta el mismo `<Outlet/>`, así que
    `PrimerosPasosPage` no se entera de nada.

    SE ESPERA A QUE LAS SOCIEDADES CARGUEN, y esto es el defecto de esta casa
    otra vez: `entities` arranca en `[]` —que significa «todavía no lo sé»— y
    leerlo como «no tiene ninguna» pintaría el traspaso desnudo durante un
    instante para luego montar la aplicación entera encima. Un parpadeo en la
    primera pantalla del producto.
  */
  if (entitiesLoading) {
    return (
      <div className="grid min-h-screen place-items-center bg-background">
        <p className="text-2xs text-muted-foreground">{t("Un momento…")}</p>
      </div>
    )
  }
  if (entities.length > 0) return <AppLayout />

  async function salir() {
    await signOut()
    navigate("/login", { replace: true })
  }

  return (
    <I18nProvider country={pais} idiomaDelUsuario={profile?.idioma ?? null}>
      <VozProvider>
        <div className="relative min-h-screen overflow-x-hidden bg-background">
          <div aria-hidden="true" className="rayado pointer-events-none absolute inset-0" />

          {/*
            El soporte es la columna, como en el login: con `min-h-screen` aquí,
            la línea de margen va de arriba abajo de la pantalla y no de una caja.
          */}
          <div className="relative mx-auto flex min-h-screen w-full max-w-[64rem] flex-col px-5 py-8 sm:px-8">
            <LineaDeMargen left="1.25rem" grosor="2px" className="sm:left-8" />

            <div className="flex min-h-0 flex-1 flex-col pl-8">
              <header className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2">
                <Logotype variante="compacto" tamano="lg" className="h-auto" />
                {/*
                  EL CLIENTE SE PUEDE CAMBIAR DESDE AQUÍ, Y ANTES NO.

                  Este encabezado sólo decía el nombre del cliente y ofrecía
                  «Cerrar sesión». Como esta pantalla es la que se pinta cuando
                  el cliente **no tiene ninguna sociedad**, pasaba esto: quien
                  cambiaba a un cliente vacío —para empezar su traspaso, o para
                  mirar cómo se ve— se quedaba dentro **sin forma de volver**, y
                  cerrar sesión no servía porque el cliente activo se guarda en
                  `localStorage` y se recupera al entrar.

                  Es la misma familia que los puntos 14 a 20 de Cesar: el
                  traspaso no te puede echar fuera, y tampoco te puede encerrar.

                  El desplegable sólo aparece con más de un cliente en la
                  cartera: con uno solo no hay nada que elegir y sería un mando
                  que no mueve nada.
                */}
                {(profile?.memberships.length ?? 0) > 1 ? (
                  <Select
                    value={activeTenantId ?? undefined}
                    onValueChange={setActiveTenantId}
                  >
                    <SelectTrigger
                      aria-label={t("Cliente activo")}
                      className="h-7 w-auto gap-1.5 border-none bg-transparent px-1.5 text-xs text-muted-foreground hover:bg-row-hover"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {profile?.memberships.map((m) => (
                        <SelectItem key={m.tenantId} value={m.tenantId}>
                          {m.tenantName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : activeMembership?.tenantName ? (
                  <span className="text-xs text-muted-foreground">
                    {activeMembership.tenantName}
                  </span>
                ) : null}

                <div className="ml-auto flex items-center gap-1.5">
                  {/*
                    LA PUERTA, SÓLO CUANDO YA NO FALTA NADA. Mientras quede algo
                    pendiente no se ofrece: no es que esté prohibido salir —los
                    propios pasos enlazan a las pantallas donde se trabaja—, es
                    que ofrecerlo aquí convertiría el cuestionario en opcional
                    justo para quien más lo necesita.
                  */}
                  {terminado ? (
                    <Button size="sm" onClick={() => navigate("/dashboard")}>
                      {t("Ir a la aplicación")}
                      <ArrowRight />
                    </Button>
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={() => void salir()}>
                    <LogOut />
                    {t("Cerrar sesión")}
                  </Button>
                </div>
              </header>

              {/*
                LA NOTA DE MARTY SE VIENE CON EL TRASPASO, y no es un adorno que
                se arrastra: `PrimerosPasosPage` NO lleva descripción propia a
                propósito —«el layout ya monta la nota de Marty con la apertura
                de la sección»— porque tenerla dos veces pondría dos párrafos
                idénticos uno sobre otro. Sacar la pantalla de `AppLayout` sin
                traerse esto la habría dejado muda justo en la única pantalla
                donde Marty tiene que hablar primero.
              */}
              <MartyNota className="mb-3" />

              {/*
                La red por pantalla, igual que en `AppLayout`: si el cuestionario
                revienta, esto sigue teniendo cabecera y salida.
              */}
              <ErrorBoundary ambito="/primeros-pasos">
                <Outlet />
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </VozProvider>
    </I18nProvider>
  )
}
